<template>
  <div :class="wrapperClasses">
    <label v-if="label" :for="selectId" class="ui-multiselect__label">
      {{ label }}
      <span v-if="required" class="ui-multiselect__required">*</span>
    </label>
    <div class="ui-multiselect__container" ref="containerRef">
      <div
        :id="selectId"
        :class="triggerClasses"
        @click="toggleDropdown"
        @focus="handleFocus"
        @blur="handleBlur"
        tabindex="0"
      >
        <div class="ui-multiselect__selected">
          <template v-if="selectedOptions.length > 0">
            <template v-if="compact">
              <span class="ui-multiselect__compact-text">{{ selectedOptions[0].label }}</span>
              <span v-if="selectedOptions.length > 1" class="ui-multiselect__compact-badge">
                +{{ selectedOptions.length - 1 }}
              </span>
            </template>
            <template v-else>
              <span
                v-for="(option, index) in displaySelected"
                :key="`${option.value}-${index}`"
                class="ui-multiselect__chip"
              >
                {{ option.label }}
                <button
                  type="button"
                  class="ui-multiselect__chip-remove"
                  @click.stop="removeOption(option.value)"
                  :disabled="disabled"
                >
                  <Icon icon="mdi:close" />
                </button>
              </span>
              <span v-if="remainingCount > 0" class="ui-multiselect__more">
                +{{ remainingCount }} more
              </span>
            </template>
          </template>
          <span v-else class="ui-multiselect__placeholder">{{ placeholder }}</span>
        </div>
        <div class="ui-multiselect__icon">
          <Icon icon="mdi:chevron-down" :class="{ 'ui-multiselect__icon--rotated': isOpen }" />
        </div>
      </div>

      <Teleport to="body">
        <div
          v-if="isOpen"
          class="ui-multiselect__dropdown"
          :style="dropdownStyle"
          ref="dropdownRef"
        >
          <div v-if="searchable" class="ui-multiselect__search">
            <Icon icon="mdi:magnify" class="ui-multiselect__search-icon" />
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="searchPlaceholder"
              class="ui-multiselect__search-input"
              @click.stop
            />
          </div>
          <div class="ui-multiselect__options" ref="optionsRef">
            <template
              v-for="item in groupedItems"
              :key="item._groupHeader ? `header-${item.group}` : item.value"
            >
              <div v-if="item._groupHeader" class="ui-multiselect__group-header">
                {{ item.group }}
              </div>
              <div
                v-else
                :class="[
                  'ui-multiselect__option',
                  {
                    'ui-multiselect__option--selected': isSelected(item.value),
                    'ui-multiselect__option--disabled': item.disabled,
                  },
                ]"
                @click.stop="toggleOption(item)"
              >
                <div class="ui-multiselect__option-checkbox">
                  <Icon
                    v-if="isSelected(item.value)"
                    icon="mdi:check"
                    width="12"
                    height="12"
                    class="ui-multiselect__option-check"
                  />
                </div>
                <span class="ui-multiselect__option-label">{{ item.label }}</span>
                <span
                  v-if="item.description"
                  class="ui-multiselect__info-icon"
                  :title="item.description"
                  @click.stop
                >
                  <Icon icon="mdi:information-outline" width="14" height="14" />
                </span>
              </div>
            </template>
            <div v-if="filteredOptions.length === 0" class="ui-multiselect__no-results">
              {{ noResultsText }}
            </div>
          </div>
          <div v-if="showSelectAll" class="ui-multiselect__footer">
            <button type="button" class="ui-multiselect__select-all" @click.stop="toggleSelectAll">
              {{ selectAllText }}
            </button>
          </div>
        </div>
      </Teleport>
    </div>
    <div v-if="error || helperText" class="ui-multiselect__helper">
      <span v-if="error" class="ui-multiselect__error">{{ error }}</span>
      <span v-else-if="helperText" class="ui-multiselect__helper-text">{{ helperText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { Teleport, computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { SelectOption, Size } from "./types";

interface Props {
  modelValue?: (string | number)[];
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  compact?: boolean;
  maxDisplayChips?: number;
  showSelectAll?: boolean;
  selectAllText?: string;
  noResultsText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  placeholder: "Select options",
  size: "md",
  disabled: false,
  required: false,
  searchable: false,
  searchPlaceholder: "Search...",
  compact: false,
  maxDisplayChips: 3,
  showSelectAll: false,
  selectAllText: "Select All",
  noResultsText: "No options found",
});

const emit = defineEmits<{
  "update:modelValue": [value: (string | number)[]];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
  change: [value: (string | number)[]];
}>();

const isOpen = ref(false);
const isFocused = ref(false);
const searchQuery = ref("");
const dropdownStyle = ref<Record<string, string>>({});
const containerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const optionsRef = ref<HTMLElement | null>(null);

const selectId = computed(
  () => props.id || `multiselect-${Math.random().toString(36).slice(2, 11)}`
);

const selectedValues = computed(() => props.modelValue || []);

const selectedOptions = computed(() => {
  return props.options.filter((option) => selectedValues.value.includes(option.value));
});

const displaySelected = computed(() => {
  return selectedOptions.value.slice(0, props.maxDisplayChips);
});

const remainingCount = computed(() => {
  return Math.max(0, selectedOptions.value.length - props.maxDisplayChips);
});

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return props.options;
  }
  const query = searchQuery.value.toLowerCase();
  return props.options.filter((option) => option.label.toLowerCase().includes(query));
});

// Build grouped items: inserts group headers before each new group
const groupedItems = computed(() => {
  const items: any[] = [];
  let lastGroup: string | undefined = undefined;
  for (const opt of filteredOptions.value) {
    if (opt.group && opt.group !== lastGroup) {
      items.push({ _groupHeader: true, group: opt.group });
      lastGroup = opt.group;
    }
    items.push(opt);
  }
  return items;
});

const isSelected = (value: string | number) => {
  return selectedValues.value.includes(value);
};

const selectAllOptions = computed(() => {
  return filteredOptions.value.filter((opt) => !opt.disabled);
});

const areAllSelected = computed(() => {
  if (selectAllOptions.value.length === 0) return false;
  return selectAllOptions.value.every((opt) => isSelected(opt.value));
});

const wrapperClasses = computed(() => {
  const classes = ["ui-multiselect"];
  classes.push(`ui-multiselect--${props.size}`);
  if (props.disabled) classes.push("ui-multiselect--disabled");
  if (props.error) classes.push("ui-multiselect--error");
  return classes;
});

const triggerClasses = computed(() => {
  const classes = ["ui-multiselect__trigger"];
  if (isFocused.value || isOpen.value) classes.push("ui-multiselect__trigger--focused");
  if (props.disabled) classes.push("ui-multiselect__trigger--disabled");
  return classes;
});

const toggleOption = (option: SelectOption) => {
  if (props.disabled || option.disabled) return;

  const currentValues = [...selectedValues.value];
  const index = currentValues.indexOf(option.value);

  if (index > -1) {
    currentValues.splice(index, 1);
  } else {
    currentValues.push(option.value);
  }

  emit("update:modelValue", currentValues);
  emit("change", currentValues);
};

const removeOption = (value: string | number) => {
  if (props.disabled) return;

  const currentValues = selectedValues.value.filter((v) => v !== value);
  emit("update:modelValue", currentValues);
  emit("change", currentValues);
};

const toggleSelectAll = () => {
  if (props.disabled) return;

  let newValues: (string | number)[];
  if (areAllSelected.value) {
    // Deselect all filtered options
    const filteredValues = selectAllOptions.value.map((opt) => opt.value);
    newValues = selectedValues.value.filter((v) => !filteredValues.includes(v));
  } else {
    // Select all filtered options
    const filteredValues = selectAllOptions.value.map((opt) => opt.value);
    const existingValues = selectedValues.value.filter((v) => !filteredValues.includes(v));
    newValues = [...existingValues, ...filteredValues];
  }

  emit("update:modelValue", newValues);
  emit("change", newValues);
};

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = "";
    nextTick(() => {
      updateDropdownPosition();
    });
  }
};

const updateDropdownPosition = () => {
  if (!containerRef.value) return;

  const rect = containerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = dropdownRef.value?.offsetHeight || 300;
  const dropdownWidth = Math.max(rect.width, 280);

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  const maxLeft = Math.max(spacing, window.innerWidth - dropdownWidth - spacing);
  const clampedLeft = Math.min(Math.max(spacing, rect.left), maxLeft);

  const topPosition = shouldShowAbove
    ? Math.max(spacing, rect.top - dropdownHeight - spacing)
    : Math.min(window.innerHeight - dropdownHeight - spacing, rect.bottom + spacing);

  dropdownStyle.value = {
    position: "fixed",
    top: `${topPosition}px`,
    left: `${clampedLeft}px`,
    width: `${dropdownWidth}px`,
    zIndex: "10070",
  };
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (
    target &&
    !target.closest(".ui-multiselect__container") &&
    !target.closest(".ui-multiselect__dropdown")
  ) {
    isOpen.value = false;
  }
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".ui-multiselect__container") &&
    !relatedTarget.closest(".ui-multiselect__dropdown")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      isFocused.value = false;
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      isFocused.value = false;
      emit("blur", event);
    }, 100);
  }
};

watch(isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
});

watch(
  () => props.options,
  () => {
    if (isOpen.value) {
      nextTick(() => {
        updateDropdownPosition();
      });
    }
  },
  { deep: true }
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});
</script>

<style scoped>
.ui-multiselect {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ui-multiselect__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
}

.ui-multiselect__required {
  color: var(--color-danger);
  font-size: 1.1rem;
  margin-left: 2px;
}

.ui-multiselect__container {
  position: relative;
  display: flex;
  align-items: center;
}

.ui-multiselect__trigger {
  width: 100%;
  min-height: var(--input-height-md);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  background-color: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: calc(var(--radius) - 2px);
  padding: 6px 12px;
  transition: all var(--transition-normal);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  position: relative;
}

.ui-multiselect__trigger:hover:not(.ui-multiselect__trigger--disabled) {
  border-color: var(--color-border-hover);
}

.ui-multiselect__trigger--focused {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.ui-multiselect__trigger--disabled {
  background-color: var(--color-bg-disabled);
  cursor: not-allowed;
  opacity: 0.5;
  border-color: var(--color-border-light);
}

.ui-multiselect__selected {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-height: 28px;
  align-items: center;
}

.ui-multiselect__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  background-color: var(--color-bg-hover);
  color: var(--color-primary);
  border: 1px solid rgba(62, 62, 254, 0.2);
  border-radius: calc(var(--radius) - 2px);
  padding: var(--space-1) var(--space-2);
  font-size: var(--font-size-xs);
  font-weight: 500;
  white-space: nowrap;
}

.ui-multiselect__chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 2px;
  color: var(--color-primary);
  opacity: 0.7;
  transition: opacity var(--transition-fast);
  font-size: 14px;
}

.ui-multiselect__chip-remove:hover {
  opacity: 1;
}

.ui-multiselect__chip-remove:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.ui-multiselect__more {
  font-size: var(--font-size-xs);
  color: var(--color-text-placeholder);
  font-weight: 500;
}

.ui-multiselect__compact-text {
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.ui-multiselect__compact-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-primary);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.ui-multiselect__placeholder {
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.ui-multiselect__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-placeholder);
  font-size: 1.25rem;
  transition: transform var(--transition-normal);
  flex-shrink: 0;
  margin-left: auto;
  padding-left: var(--space-2);
}

.ui-multiselect__icon--rotated {
  transform: rotate(180deg);
}

.ui-multiselect__trigger--focused .ui-multiselect__icon {
  color: var(--color-primary);
}

.ui-multiselect__dropdown {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 300px;
  padding: var(--space-1);
}

.ui-multiselect__search {
  position: relative;
  padding: var(--space-2);
  border-bottom: 1px solid var(--color-border-light);
}

.ui-multiselect__search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-placeholder);
  font-size: 1.1rem;
  pointer-events: none;
}

.ui-multiselect__search-input {
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid var(--color-border);
  border-radius: calc(var(--radius) - 2px);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--color-text);
  background-color: var(--color-bg-white);
  transition: all var(--transition-normal);
}

.ui-multiselect__search-input:focus {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.ui-multiselect__options {
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
}

.ui-multiselect__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  transition: background-color var(--transition-fast);
  border-radius: calc(var(--radius) - 2px);
}

.ui-multiselect__option:hover:not(.ui-multiselect__option--disabled) {
  background-color: var(--color-bg-hover);
}

.ui-multiselect__option--selected {
  background-color: var(--color-bg-selected);
}

.ui-multiselect__option--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ui-multiselect__option-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 2px solid var(--color-border);
  border-radius: calc(var(--radius) - 4px);
  background-color: var(--color-bg-white);
  transition: all var(--transition-normal);
  flex-shrink: 0;
}

.ui-multiselect__option--selected .ui-multiselect__option-checkbox {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}

.ui-multiselect__option-check {
  color: #ffffff;
  flex-shrink: 0;
}

.ui-multiselect__option-label {
  font-size: var(--font-size-sm);
  color: #344054;
  font-weight: 400;
  flex: 1;
}

.ui-multiselect__group-header {
  padding: 6px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  pointer-events: none;
  user-select: none;
}

.ui-multiselect__group-header:not(:first-child) {
  margin-top: 4px;
  border-top: 1px solid var(--color-border-light);
  padding-top: 8px;
}

.ui-multiselect__info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: help;
  flex-shrink: 0;
  margin-left: 2px;
}

.ui-multiselect__info-icon:hover {
  color: var(--color-primary);
}

.ui-multiselect__option--selected .ui-multiselect__option-label {
  font-weight: 500;
  color: #1e293b;
}

.ui-multiselect__no-results {
  padding: 20px 12px;
  text-align: center;
  color: var(--color-text-placeholder);
  font-size: var(--font-size-sm);
}

.ui-multiselect__footer {
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--color-border-light);
  background-color: var(--color-bg-muted);
}

.ui-multiselect__select-all {
  width: 100%;
  padding: var(--space-2);
  background: none;
  border: 1px solid var(--color-border);
  border-radius: calc(var(--radius) - 2px);
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--color-primary);
  cursor: pointer;
  transition: all var(--transition-normal);
  font-family: var(--font-family);
}

.ui-multiselect__select-all:hover {
  background-color: var(--color-bg-hover);
  border-color: var(--color-primary);
}

.ui-multiselect__helper {
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
}

.ui-multiselect__error {
  color: var(--color-danger);
}

.ui-multiselect__helper-text {
  color: var(--color-text-placeholder);
}

/* Size: sm */
.ui-multiselect--sm .ui-multiselect__label {
  font-size: 11px;
}

.ui-multiselect--sm .ui-multiselect__trigger {
  min-height: var(--input-height-sm);
  padding: 4px 10px;
  font-size: var(--font-size-xs);
}

.ui-multiselect--sm .ui-multiselect__chip {
  padding: 2px 6px;
  font-size: 0.7rem;
}

.ui-multiselect--sm .ui-multiselect__icon {
  font-size: 1rem;
}

/* Size: lg */
.ui-multiselect--lg .ui-multiselect__label {
  font-size: 13px;
}

.ui-multiselect--lg .ui-multiselect__trigger {
  min-height: var(--input-height-lg);
  padding: 8px 16px;
  font-size: 0.9rem;
}

.ui-multiselect--lg .ui-multiselect__chip {
  padding: 6px 10px;
  font-size: 0.8rem;
}

.ui-multiselect--lg .ui-multiselect__icon {
  font-size: 1.5rem;
}

/* Error state */
.ui-multiselect--error .ui-multiselect__trigger {
  border-color: var(--color-danger);
}

.ui-multiselect--error .ui-multiselect__trigger--focused {
  border-color: var(--color-danger) !important;
  box-shadow: var(--ring-danger) !important;
}
</style>
